/* eslint-disable react/prop-types */
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  getHangerState,
  getJobSettingsState,
  getJoistState,
  getResultState,
  getSettingsState,
} from '../../../store/selectors'
import { getFormErrors } from '../../../store/slices'
import { NoticeMessage } from '../../../../../components'
import { RenderAorB, isEmpty } from '../../../../../utils/fns'
import { warningList } from '../../../common/json/helpText.json'
import { HANGER_TYPE, JOIST_MEMBER_TYPE } from '../../../common/constants'
import { COUNTRY, LOADING } from '../../../../../utils/constants'
import { TableNotes } from '../../../views/Output/components/Result/components'
import ResultTable from './PrintResultTable'
import { IndeterminateSelectBox } from '../../../components'
import {
  getFastenersDisplayLabel,
  displayFastenersAnchor,
  updateProductSpec,
  getFastenersLabel,
} from '../../../views/Output/utils/fastener'
import { getICILabel, isInvalidOffsetDirection } from '../../../common/fns'

const Result = ({ jobItem }) => {
  const hangerType = useSelector((state) => getJobSettingsState(state).hangerType)
  const joistMemberType = useSelector((state) => getJoistState(state).type)
  const { topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, hlcFlush, topFlangeOffset } =
    useSelector(getHangerState)
  const errors = useSelector(getFormErrors)
  const country = useSelector((state) => getSettingsState(state).country)

  const { loading, renderedData, error, searchKeyword, optimized, selectedRow } =
    useSelector(getResultState)

  const inputErrors = useMemo(() => {
    return Object.values(errors).join('<br />')
  }, [errors])

  const hangerNotTopFlangeMsg = useMemo(() => {
    if (
      (hangerType === HANGER_TYPE.ALL_TYPES &&
        Number(topFlangeOpenClosedAngle) + Number(topFlangeSlopedDownAngle) !== 0) ||
      isInvalidOffsetDirection(hangerType, topFlangeOffset)
    ) {
      return warningList.hangerNotTopFlange
    } else {
      return null
    }
  }, [hangerType, topFlangeOpenClosedAngle, topFlangeSlopedDownAngle, topFlangeOffset])

  const columns = useMemo(() => {
    return [
      {
        id: 'selection',
        Header: () => null,
        Cell: ({ row }) => {
          return (
            <div className="d-flex justify-center">
              {
                (jobItem && row.id === jobItem.id) || (selectedRow && selectedRow.id === row.id) ? (
                <div className="hs__modelChecked">&nbsp;</div>
              ) : (
                <IndeterminateSelectBox type="radio" readOnly />
                )
              }
            </div>
          )
        },
      },
      {
        id: 'model',
        Header: 'Model',
        accessor: 'model',
        Cell: ({ value, row }) => {
          const { seriesIllustration, updatedWebStiffeners, updatedSpec } = row.original
          return (
            <div className="hs__firstCell">
              <div className="hs__modelImage">
                <img src={seriesIllustration} alt={value} />
              </div>

              <div className="hs__modelInfo">
                <span className="button__text">
                  {value}
                  {updatedWebStiffeners}
                  <br />
                  {updatedSpec}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        Header: 'Installed Cost',
        accessor: 'ici',
        Cell: ({ row }) => <span>{row.values.iciLabel}</span>,
      },
      {
        Header: 'Width',
        accessor: 'wSize',
        sortType: 'basic',
      },
      {
        Header: 'Height',
        accessor: 'hSize',
        sortType: 'basic',
      },
      {
        Header: 'Bearing',
        accessor: 'bSize',
        sortType: 'basic',
      },
      {
        id: 'tfSize',
        Header: 'TF Depth',
        accessor: (data) => (data.tfSize === 0 ? '-' : data.tfSize),
        sortType: 'basic',
      },
      {
        id: 'tfFastenersLabel',
        Header: 'TF Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.tfNailQty, d.tfNailType),
        Cell: ({ value, row }) => {
          const { tfNailType } = row.original
          return displayFastenersAnchor(tfNailType, value)
        },
      },
      {
        id: 'fFastenersLabel',
        Header: 'Face Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.hNailQty[0], d.hNailType[0]),
        Cell: ({ value, row }) => {
          const { hNailType } = row.original
          return displayFastenersAnchor(hNailType[0], value)
        },
      },
      {
        id: 'jFastenersLabel',
        Header: 'Joist Fasteners',
        accessor: (d) => getFastenersDisplayLabel(d.jNailQty, d.jNailType, true),
        Cell: ({ value, row }) => {
          const { jNailType } = row.original
          return displayFastenersAnchor(jNailType, value)
        },
      },
      {
        Header: 'Download (lbs)',
        accessor: 'load',
      },
      {
        Header: 'Uplift (lbs)',
        accessor: 'uplift',
      },
      {
        Header: () => null,
        accessor: 'tfNailQty',
      },
      {
        Header: () => null,
        accessor: 'tfNailType',
      },
      {
        Header: () => null,
        accessor: 'hNailQty',
      },
      {
        Header: () => null,
        accessor: 'hNailType',
      },
      {
        Header: () => null,
        accessor: 'jNailQty',
      },
      {
        Header: () => null,
        accessor: 'jNailType',
      },
      {
        id: 'updatedSpec',
        Header: () => null,
        accessor: (d) => {
          const hlc = jobItem ? jobItem.hlcFlush : hlcFlush
          const updatedSpec = updateProductSpec(d.spec, hlc, hangerType)

          return updatedSpec
        },
      },
      {
        id: 'updatedWebStiffeners',
        Header: () => null,
        accessor: (d) => {
          const updatedWebStiffeners = d.webStiffeners ? '*' : ''

          return updatedWebStiffeners
        },
      },
      {
        id: 'iciLabel',
        Header: () => null,
        accessor: (d) => getICILabel(d.ici),
      },
    ]
  }, [])

  const getRowId = (row, relativeIndex, parent) => {
    const tfFasteners = getFastenersLabel(row.tfNailQty, row.tfNailType)
    const fFasteners = getFastenersLabel(row.hNailQty[0], row.hNailType[0])
    const jFasteners = getFastenersLabel(row.jNailQty, row.jNailType, true)

    return parent
      ? [parent.id, relativeIndex].join('.')
      : [row.model, tfFasteners, fFasteners, jFasteners, row.load, row.uplift].join('-')
  }

  return (
    <div className="space-y-4 my-4">
      {RenderAorB(
        jobItem?.outputData,
        <>
          <ResultTable
            data={jobItem?.outputData}
            columns={columns}
            optimized={jobItem?.jobInputs?.result?.optimized}
            searchKeyword={jobItem?.jobInputs?.result?.searchKeyword}
            getRowId={getRowId}
          />
          <TableNotes
            isIJoistType={jobItem?.jobInputs?.joist?.type === JOIST_MEMBER_TYPE.IJOIST}
            isUSA={jobItem?.jobInputs?.settings?.country === COUNTRY.USA}
          />
        </>,
        RenderAorB(
          loading === LOADING.FAILED,
          <NoticeMessage level="error" icon="error" title="Network Error" description={error} />,
          RenderAorB(
            inputErrors,
            <NoticeMessage
              level="error"
              icon="error"
              title="Validation"
              description={inputErrors}
            />,
            RenderAorB(
              loading === LOADING.SUCCEEDED && isEmpty(renderedData),
              <NoticeMessage
                level="warning"
                icon="warning"
                title="No Output Results"
                description="There are no results based upon the input parameters. Try editing your input criteria, or use the reset button to load the initial conditions."
              />,
              <>
                <NoticeMessage
                  level="warning"
                  icon="warning"
                  title="Warning"
                  description={hangerNotTopFlangeMsg}
                />

                <ResultTable
                  data={renderedData}
                  columns={columns}
                  optimized={optimized}
                  searchKeyword={searchKeyword}
                  getRowId={getRowId}
                />
                <TableNotes
                  isIJoistType={joistMemberType === JOIST_MEMBER_TYPE.IJOIST}
                  isUSA={country === COUNTRY.USA}
                />
              </>,
            ),
          ),
        ),
      )}
    </div>
  )
}

export default Result
